import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { ErrorState, cardSurfaceSx } from '@eis/patterns';
import { assetOfErn, isGroupSourced, manifestRef } from '../../../model/deckung-overview';
import { useDeckungNavigation } from './navigation';
import { useVersionDeckung, type DeckungOwner } from './version-deckung';

/**
 * "Tarif & Deckung" on a version page (SDD-018 §5: generation and version pages show the pinned
 * variations and claims configuration): the variants the version offers (label + key), the
 * claims configuration's currency and settlement type, and a link to the pinned asset.
 *
 * A group version shows the RESOLVED variants — the child's document under the group's override —
 * and links its OWN pin, the override; without one it says so and the variants are the child's.
 */
export function PinnedDeckungCard({
  owner,
  ern,
  manifest,
  draft,
}: {
  readonly owner: DeckungOwner;
  readonly ern: string;
  readonly manifest: unknown;
  readonly draft: boolean;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const { openAsset } = useDeckungNavigation();
  const deckung = useVersionDeckung(owner, ern, manifest, draft);
  const pin = assetOfErn(manifestRef(manifest, 'tarifDeckung'));
  const doc = deckung.doc;
  const setting = doc?.claimConfig.generalSetting;

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={`pinned-deckung-${ern}`}
      sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Typography variant="h4" component="h2" id={`pinned-deckung-${ern}`}>
            {t('deckung.pinned.title')}
          </Typography>
          {pin !== undefined && (
            <Button
              size="small"
              endIcon={<OpenInNewIcon />}
              onClick={() => {
                openAsset({ owner, ...pin });
              }}
            >
              {t('deckung.pinned.open')}
            </Button>
          )}
        </Stack>

        {pin === undefined ? (
          <Typography variant="body2" color="text.secondary">
            {t('deckung.pinned.none')}
          </Typography>
        ) : (
          <Box
            component="code"
            sx={{ fontFamily: 'monospace', typography: 'caption', color: 'text.secondary' }}
          >
            {manifestRef(manifest, 'tarifDeckung')}
          </Box>
        )}

        {deckung.state === 'loading' ? (
          <Skeleton variant="rounded" height={56} />
        ) : deckung.state === 'error' ? (
          <ErrorState
            description={t('deckung.generations.loadError')}
            details={deckung.error?.message}
            onRetry={deckung.retry}
          />
        ) : doc === undefined ? null : (
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Typography variant="body2" color="text.secondary" component="span">
                {t('deckung.pinned.variants')}
              </Typography>
              {owner === 'group' && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={
                    isGroupSourced(deckung.sources, 'allowedVariants') ? 'secondary' : 'default'
                  }
                  label={
                    isGroupSourced(deckung.sources, 'allowedVariants')
                      ? t('deckung.overview.groupMarker')
                      : t('deckung.group.fromTarif')
                  }
                />
              )}
            </Stack>
            {doc.tariffVariations.length === 0 ? (
              <Typography variant="body2">{t('deckung.variants.noVariants')}</Typography>
            ) : (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                component="ul"
                sx={{ flexWrap: 'wrap', listStyle: 'none', m: 0, p: 0 }}
              >
                {doc.tariffVariations.map((variant) => (
                  <li key={variant.key}>
                    <Chip size="small" label={`${variant.info.label} · ${variant.key}`} />
                  </li>
                ))}
              </Stack>
            )}
            {setting !== undefined && (
              <Typography variant="body2" color="text.secondary">
                {t('deckung.pinned.claimConfigLine', {
                  currency: t(`deckung.claimConfig.currency.${setting.currency}`),
                  settlement: t(`deckung.claimConfig.settlementType.${setting.settlementType}`),
                })}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
